const chapter = {
  id: 'oscillations-waves',
  slug: 'oscillations-waves',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Oscillations & Waves',
        hindi: 'दोलन और तरंगें',
  },
  estReadMinutes: 70,
  difficulty: {
    english: 'Hard',
        hindi: 'कठिन',
  },
  weightage: {
    english: '3–4 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 3-4 प्रश्न',
  },
  syllabusTags: ['Oscillations', 'Waves', 'JEE-Main', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'shm',
      title: {
        english: 'Simple Harmonic Motion (SHM)',
        hindi: 'सरल आवर्त गति (SHM)',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'SHM',
        hindi: 'एसएचएम',
          },
          body: {
            english: 'Linear oscillation in which restoring force is proportional to and opposite displacement: $F = -kx$, equivalently $\\ddot x = -\\omega^2 x$ with $\\omega = \\sqrt{k/m}$.',
        hindi: 'रैखिक दोलन जिसमें पुनर्स्थापन बल विस्थापन के समानुपाती और विपरीत होता है: $F = -kx$, समकक्ष $\\ddot x = -\\omega^2 x$ $\\omega = \\sqrt{k/m}$ के साथ।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'General solution',
        hindi: 'सामान्य समाधान',
          },
          statement: {
            english: '$x(t) = A\\sin(\\omega t + \\phi)$. **Period** $T = 2\\pi/\\omega = 2\\pi\\sqrt{m/k}$. Velocity $v = \\omega\\sqrt{A^2 - x^2}$. Acceleration $a = -\\omega^2 x$.',
        hindi: '$x(t) = A\\sin(\\omega t + \\phi)$. **अवधि** $T = 2\\pi/\\omega = 2\\pi\\sqrt{m/k}$. वेग $v = \\omega\\sqrt{A^2 - x^2}$. त्वरण $a = -\\omega^2 x$.',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Energy: KE $= \\tfrac12 m\\omega^2 (A^2 - x^2)$, PE $= \\tfrac12 m\\omega^2 x^2$, total $E = \\tfrac12 m\\omega^2 A^2 = \\tfrac12 k A^2$.',
        hindi: 'ऊर्जा: KE $= \\tfrac12 m\\omega^2 (A^2 - x^2)$, PE $= \\tfrac12 m\\omega^2 x^2$, कुल $E = \\tfrac12 m\\omega^2 A^2 = \\tfrac12 k A^2$।',
            },
            {
              english: 'KE = PE at $x = A/\\sqrt 2$.',
        hindi: '$x = A/\\sqrt 2$ पर KE = PE।',
            },
            {
              english: 'Phase difference: $v$ leads $x$ by $\\pi/2$; $a$ leads $x$ by $\\pi$.',
        hindi: 'चरण अंतर: $v$ $x$ को $\\pi/2$ से आगे बढ़ाता है; $a$ $\\pi$ से $x$ से आगे है।',
            },
          ],
        },
      ],
    },
    {
      id: 'shm-systems',
      title: {
        english: 'Common SHM Systems',
        hindi: 'सामान्य SHM निकाय',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Spring-mass**: $T = 2\\pi\\sqrt{m/k}$.',
        hindi: '**स्प्रिंग-मास**: $T = 2\\pi\\sqrt{m/k}$।',
            },
            {
              english: '**Simple pendulum** (small angles): $T = 2\\pi\\sqrt{L/g}$.',
        hindi: '**सरल पेंडुलम** (छोटे कोण): $T = 2\\pi\\sqrt{L/g}$।',
            },
            {
              english: '**Physical (compound) pendulum**: $T = 2\\pi\\sqrt{I/(mgd)}$, $d$ = distance from pivot to COM.',
        hindi: '**भौतिक (यौगिक) पेंडुलम**: $T = 2\\pi\\sqrt{I/(mgd)}$, $d$ = धुरी से COM तक की दूरी।',
            },
            {
              english: '**Torsional**: $T = 2\\pi\\sqrt{I/\\kappa}$.',
        hindi: '**मरोड़**: $T = 2\\pi\\sqrt{I/\\kappa}$।',
            },
            {
              english: 'Springs in **series**: $1/k_{\\text{eq}} = 1/k_1 + 1/k_2$. **Parallel**: $k_{\\text{eq}} = k_1 + k_2$.',
        hindi: '**श्रृंखला** में स्प्रिंग्स: $1/k_{\\text{eq}} = 1/k_1 + 1/k_2$। **समानांतर**: $k_{\\text{eq}} = k_1 + k_2$.',
            },
            {
              english: 'Pendulum in lift accelerating up ($a$): $T = 2\\pi\\sqrt{L/(g+a)}$. Free fall: $T \\to \\infty$.',
        hindi: 'लिफ्ट में पेंडुलम तेजी से बढ़ रहा है ($a$): $T = 2\\pi\\sqrt{L/(g+a)}$। मुक्त गिरावट: $T \\to \\infty$।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Time period of a simple pendulum of length 1 m on Earth ($g = \\pi^2$ m/s² approx).',
        hindi: 'पृथ्वी पर 1 मीटर लंबाई के एक सरल लोलक की समयावधि ($g = \\pi^2$ m/s² लगभग)।',
          },
          solution: {
            english: '$T = 2\\pi\\sqrt{L/g} = 2\\pi\\sqrt{1/\\pi^2} = 2$ s.',
        hindi: '$T = 2\\pi\\sqrt{L/g} = 2\\pi\\sqrt{1/\\pi^2} = 2$ s.',
          },
        },
      ],
    },
    {
      id: 'damped-forced',
      title: {
        english: 'Damped, Forced Oscillations & Resonance',
        hindi: 'अवमन्दित, प्रणोदित दोलन और अनुनाद',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Damped SHM**: $m\\ddot x + b\\dot x + kx = 0$. Underdamped solution: $x = A_0 e^{-bt/(2m)}\\cos(\\omega\' t + \\phi)$ with $\\omega\' = \\sqrt{\\omega_0^2 - (b/2m)^2}$.',
        hindi: '**नम एसएचएम**: $m\\ddot x + b\\dot x + kx = 0$। अंडरडैम्प्ड समाधान: $x = A_0 e^{-bt/(2m)}\\cos(\\omega\' t + \\phi)$ $\\omega\' = \\sqrt{\\omega_0^2 - (b/2m)^2}$ के साथ।',
            },
            {
              english: 'Energy decays as $E(t) = E_0 e^{-bt/m}$.',
        hindi: 'ऊर्जा का क्षय $E(t) = E_0 e^{-bt/m}$ के रूप में होता है।',
            },
            {
              english: '**Forced** at frequency $\\omega_d$ → steady-state amplitude peaks at $\\omega_d \\approx \\omega_0$ (**resonance**). Sharpness ↑ as damping ↓.',
        hindi: '**मजबूर** आवृत्ति पर $\\omega_d$ → स्थिर-अवस्था का आयाम $\\omega_d \\approx \\omega_0$ (**प्रतिध्वनि**) पर चरम पर होता है। कुशाग्रता ↑ भिगोना ↓ के रूप में।',
            },
          ],
        },
      ],
    },
    {
      id: 'wave-basics',
      title: {
        english: 'Wave Motion — Basics & Equation',
        hindi: 'तरंग गति — मूल बातें और समीकरण',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Wave types: **mechanical** (need medium; transverse e.g. string, longitudinal e.g. sound), **EM** (no medium needed), **matter** waves.',
        hindi: 'तरंग प्रकार: **यांत्रिक** (माध्यम की आवश्यकता; अनुप्रस्थ जैसे स्ट्रिंग, अनुदैर्ध्य जैसे ध्वनि), **ईएम** (कोई माध्यम की आवश्यकता नहीं), **पदार्थ** तरंगें। तरंग दैर्ध्य , आवृत्ति , अवधि , कोणीय आवृत्ति , तरंग संख्या । स्पीड .',
            },
            {
              english: 'Wavelength $\\lambda$, frequency $f$, period $T = 1/f$, angular freq $\\omega = 2\\pi f$, wave number $k = 2\\pi/\\lambda$. Speed $v = f\\lambda = \\omega/k$.',
        hindi: 'तरंग दैर्ध्य $\\lambda$, आवृत्ति $f$, अवधि $T = 1/f$, कोणीय आवृत्ति $\\omega = 2\\pi f$, तरंग संख्या $k = 2\\pi/\\lambda$। गति $v = f\\lambda = \\omega/k$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'Travelling wave',
        hindi: 'यात्रा तरंग',
          },
          statement: {
            english: 'A wave moving in $+x$ direction: $y(x, t) = A\\sin(kx - \\omega t + \\phi)$. Satisfies wave equation $\\dfrac{\\partial^2 y}{\\partial t^2} = v^2 \\dfrac{\\partial^2 y}{\\partial x^2}$.',
        hindi: 'एक लहर $+x$ दिशा में घूम रही है: $y(x, t) = A\\sin(kx - \\omega t + \\phi)$। तरंग समीकरण $\\dfrac{\\partial^2 y}{\\partial t^2} = v^2 \\dfrac{\\partial^2 y}{\\partial x^2}$ को संतुष्ट करता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Speed on a stretched string: $v = \\sqrt{T/\\mu}$, $\\mu$ = linear mass density.',
        hindi: 'खिंची हुई डोरी पर गति: $v = \\sqrt{T/\\mu}$, $\\mu$ = रैखिक द्रव्यमान घनत्व।',
            },
            {
              english: 'Speed of sound in fluid: $v = \\sqrt{B/\\rho}$. In ideal gas (Laplace correction): $v = \\sqrt{\\gamma RT/M}$.',
        hindi: 'द्रव में ध्वनि की गति: $v = \\sqrt{B/\\rho}$। आदर्श गैस में (लाप्लास सुधार): $v = \\sqrt{\\gamma RT/M}$.',
            },
            {
              english: 'Sound speed in air at 0°C ≈ 332 m/s; rises by ≈ 0.61 m/s per °C.',
        hindi: 'हवा में ध्वनि की गति 0°C ≈ 332 m/s; ≈ 0.61 m/s प्रति °C बढ़ जाता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'super',
      title: {
        english: 'Superposition — Interference, Beats, Standing Waves',
        hindi: 'अध्यारोपण — व्यतिकरण, विस्पन्द, अप्रगामी तरंगें',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Interference** of two coherent waves of amplitudes $A_1, A_2$, phase diff $\\phi$: resultant amplitude $A = \\sqrt{A_1^2 + A_2^2 + 2A_1 A_2 \\cos\\phi}$. Constructive: $\\phi = 2n\\pi$, $A = A_1 + A_2$. Destructive: $\\phi = (2n+1)\\pi$, $A = |A_1 - A_2|$.',
        hindi: '**आयामों की दो सुसंगत तरंगों का हस्तक्षेप** $A_1, A_2$, चरण अंतर $\\phi$: परिणामी आयाम $A = \\sqrt{A_1^2 + A_2^2 + 2A_1 A_2 \\cos\\phi}$। रचनात्मक: $\\phi = 2n\\pi$, $A = A_1 + A_2$। विनाशकारी: $\\phi = (2n+1)\\pi$, $A = |A_1 - A_2|$।',
            },
            {
              english: '**Beats**: superposition of two close-frequency waves $f_1, f_2$ → amplitude oscillates at beat frequency $|f_1 - f_2|$.',
        hindi: '**बीट्स**: दो क्लोज-फ़्रीक्वेंसी तरंगों का सुपरपोज़िशन $f_1, f_2$ → बीट फ़्रीक्वेंसी $|f_1 - f_2|$ पर आयाम दोलन करता है। **खड़ी तरंगें**: $f_1, f_2$। $|f_1 - f_2|$ पर नोड्स, पर एंटीनोड्स। नोड-से-नोड की दूरी .',
            },
            {
              english: '**Standing waves**: $y = 2A\\sin(kx)\\cos(\\omega t)$. Nodes at $kx = n\\pi$, antinodes at $kx = (n+\\tfrac12)\\pi$. Distance node-to-node $= \\lambda/2$.',
        hindi: '**खड़ी लहरें**: $y = 2A\\sin(kx)\\cos(\\omega t)$. $kx = n\\pi$ पर नोड्स, $kx = (n+\\tfrac12)\\pi$ पर एंटीनोड। नोड-से-नोड की दूरी $= \\lambda/2$.',
            },
          ],
        },
        {
          type: 'theorem',
          name: {
            english: 'String fixed at both ends',
        hindi: 'दोनों सिरों पर स्ट्रिंग तय की गई',
          },
          statement: {
            english: 'Allowed wavelengths: $\\lambda_n = 2L/n$. Frequencies $f_n = n v/(2L) = n f_1$. **Fundamental** $f_1 = v/(2L)$. All harmonics present.',
        hindi: 'अनुमत तरंग दैर्ध्य: $\\lambda_n = 2L/n$। आवृत्तियाँ $f_n = n v/(2L) = n f_1$. **मौलिक** $f_1 = v/(2L)$. सभी हार्मोनिक्स मौजूद हैं।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Air column — pipes',
        hindi: 'वायु स्तंभ - पाइप',
          },
          statement: {
            english: '**Open at both ends**: $f_n = nv/(2L)$, all harmonics. **Closed at one end**: $f_n = (2n-1)v/(4L)$, only **odd** harmonics. End correction $\\approx 0.6 r$.',
        hindi: '**दोनों सिरों पर खुला**: $f_n = nv/(2L)$, सभी हार्मोनिक्स। **एक छोर पर बंद**: $f_n = (2n-1)v/(4L)$, केवल **विषम** हार्मोनिक्स। सुधार समाप्त करें $\\approx 0.6 r$।',
          },
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Fundamental of a 50 cm string with tension 200 N and linear mass density 5 g/m.',
        hindi: 'तनाव 200 N और रैखिक द्रव्यमान घनत्व 5 g/m के साथ 50 सेमी स्ट्रिंग का मूल तत्व।',
          },
          solution: {
            english: '$v = \\sqrt{200/0.005} = 200$ m/s. $f_1 = v/(2L) = 200/1 = 200$ Hz.',
        hindi: '$v = \\sqrt{200/0.005} = 200$ मी/से. $f_1 = v/(2L) = 200/1 = 200$ हर्ट्ज।',
          },
        },
      ],
    },
    {
      id: 'doppler',
      title: {
        english: 'Doppler Effect (Sound)',
        hindi: 'डॉप्लर प्रभाव (ध्वनि)',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Doppler formula',
        hindi: 'डॉपलर सूत्र',
          },
          statement: {
            english: 'Observed frequency $f\' = f_0 \\dfrac{v + v_O}{v - v_S}$, where $v$ = speed of sound, $v_O$ = speed of observer toward source (positive), $v_S$ = speed of source toward observer (positive).',
        hindi: 'प्रेक्षित आवृत्ति $f\\\' = f_0 \\dfrac{v + v_O}{v - v_S}$, जहां $v$ = ध्वनि की गति, $v_O$ = स्रोत की ओर प्रेक्षक की गति (सकारात्मक), $v_S$ = प्रेक्षक की ओर स्रोत की गति (सकारात्मक)।',
          },
        },
        {
          type: 'example',
          number: 3,
          problem: {
            english: 'Source at rest emits 500 Hz; observer moves toward source at 30 m/s ($v = 330$). Heard frequency?',
        hindi: 'आराम पर स्रोत 500 हर्ट्ज उत्सर्जित करता है; प्रेक्षक 30 m/s ($v = 330$) पर स्रोत की ओर बढ़ता है। आवृत्ति सुनी?',
          },
          solution: {
            english: '$f\' = 500 (330 + 30)/330 = 500 \\cdot 360/330 \\approx 545.5$ Hz.',
        hindi: '$f\' = 500 (330 + 30)/330 = 500 \\cdot 360/330 \\approx 545.5$ हर्ट्ज.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'x = A\\sin(\\omega t + \\phi),\\ T = 2\\pi/\\omega',
      desc: {
        english: 'SHM',
        hindi: 'एसएचएम',
      },
    },
    {
      latex: 'T = 2\\pi\\sqrt{m/k},\\ T = 2\\pi\\sqrt{L/g}',
      desc: {
        english: 'Spring & pendulum',
        hindi: 'स्प्रिंग और पेंडुलम',
      },
    },
    {
      latex: 'E = \\tfrac12 k A^2',
      desc: {
        english: 'SHM energy',
        hindi: 'SHM ऊर्जा',
      },
    },
    {
      latex: 'v_{\\text{string}} = \\sqrt{T/\\mu}',
      desc: {
        english: 'String wave',
        hindi: 'स्ट्रिंग तरंग',
      },
    },
    {
      latex: 'v_{\\text{sound}} = \\sqrt{\\gamma RT/M}',
      desc: {
        english: 'Laplace',
        hindi: 'लाप्लास',
      },
    },
    {
      latex: 'f_n = nv/(2L)',
      desc: {
        english: 'String / open pipe',
        hindi: 'स्ट्रिंग / खुली पाइप',
      },
    },
    {
      latex: 'f\' = f_0 (v+v_O)/(v-v_S)',
      desc: {
        english: 'Doppler',
        hindi: 'डॉपलर',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'SHM',
      problem: {
        english: 'A particle in SHM has amplitude 5 cm and period 2 s. Maximum speed?',
        hindi: 'SHM में एक कण का आयाम 5 सेमी और अवधि 2 s है। अधिकतम गति?',
      },
      answer: {
        english: '$5\\pi$ cm/s',
        hindi: '$5\\pi$ cm/s',
      },
      solution: {
        english: '$v_{\\max} = A\\omega = 5 \\cdot \\pi$ cm/s.',
        hindi: '$v_{\\max} = A\\omega = 5 \\cdot \\pi$ सेमी/सेकेंड।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Pendulum',
      problem: {
        english: 'A simple pendulum has period $T$ on Earth. Period on Moon ($g_M = g/6$)?',
        hindi: 'एक साधारण पेंडुलम की पृथ्वी पर अवधि $T$ है। चंद्रमा पर अवधि ($g_M = g/6$)?',
      },
      answer: {
        english: '$\\sqrt 6\\, T$',
        hindi: '$\\sqrt 6\\, T$',
      },
      solution: {
        english: '$T \\propto 1/\\sqrt g$.',
        hindi: '$T \\propto 1/\\sqrt g$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Standing waves',
      problem: {
        english: 'A pipe closed at one end, length 1 m. Fundamental frequency? ($v = 340$ m/s)',
        hindi: 'एक सिरे पर बंद एक पाइप, लंबाई 1 मीटर। मौलिक आवृत्ति? ($v = 340$ m/s)',
      },
      answer: {
        english: '$85$ Hz',
        hindi: '$85$ हर्ट्ज',
      },
      solution: {
        english: '$f_1 = v/(4L) = 340/4 = 85$ Hz.',
        hindi: '$f_1 = v/(4L) = 340/4 = 85$ हर्ट्ज.',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Doppler',
      problem: {
        english: 'Train sounding 400 Hz horn approaches a stationary observer at 30 m/s ($v = 330$). Heard frequency?',
        hindi: '400 हर्ट्ज का हॉर्न बजाती हुई ट्रेन 30 m/s ($v = 330$) पर एक स्थिर पर्यवेक्षक के पास पहुंचती है। आवृत्ति सुनी?',
      },
      answer: {
        english: '$\\approx 440$ Hz',
        hindi: '$\\approx 440$ हर्ट्ज',
      },
      solution: {
        english: '$f\' = 400 \\cdot 330/(330-30) = 440$ Hz.',
        hindi: '$f\' = 400 \\cdot 330/(330-30) = 440$ हर्ट्ज़।',
      },
    },
    {
      year: 2020,
      exam: 'JEE Main',
      tag: 'Beats',
      problem: {
        english: 'Two tuning forks of 256 Hz and 260 Hz are sounded together. Beat frequency?',
        hindi: '256 हर्ट्ज और 260 हर्ट्ज के दो ट्यूनिंग कांटे एक साथ बजाए जाते हैं। बीट आवृत्ति?',
      },
      answer: {
        english: '$4$ Hz',
        hindi: '$4$ हर्ट्ज',
      },
      solution: {
        english: '$|f_1 - f_2|$.',
        hindi: '$|f_1 - f_2|$.',
      },
    },
  ],
  next: {
    slug: 'electrostatics',
    title: 'Electrostatics',
  },
};

export default chapter;
